/**
 * Panel de Constructoras (público, sin login).
 *
 * Flujo Data-Entry riguroso 1:N:
 *   1) Datos de la constructora + contacto
 *   2) Datos del proyecto
 *   3) Tipologías (repetidor, mínimo 1) — la fuente de verdad para el Advisor
 *   4) Material de apoyo: brochure y lista de precios (PDF o link)
 *   5) Envío → status='pending' → aprobación admin antes de salir al aire
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, Plus, Trash2, CheckCircle2, Upload, Link as LinkIcon,
  Loader2, ChevronRight, ChevronLeft, ShieldCheck, FileText, DollarSign, Sparkles,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';

const STAGES = [
  { value: 'planos', label: 'En planos' },
  { value: 'preventa', label: 'Preventa' },
  { value: 'construccion', label: 'En construcción' },
  { value: 'entrega', label: 'Entrega' },
  { value: 'entregado', label: 'Entregado' },
];

const emptyTypology = () => ({
  name: '',
  area_m2: '',
  beds: '',
  baths: '',
  parking: '',
  price_from: '',
});

const fmtCOP = (n) => {
  if (!n) return '';
  return Number(n).toLocaleString('es-CO');
};

export default function ConstructorPanel() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {id, message}

  const [data, setData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    company_website: '',
    project_name: '',
    city: '',
    neighborhood: '',
    address: '',
    stage: 'preventa',
    delivery_date: '',
    description: '',
    brochure_url: '',
    pricelist_url: '',
    typologies: [emptyTypology()],
  });

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const setTypology = (idx, field, value) => {
    setData((d) => {
      const t = [...d.typologies];
      t[idx] = { ...t[idx], [field]: value };
      return { ...d, typologies: t };
    });
  };
  const addTypology = () => setData((d) => ({ ...d, typologies: [...d.typologies, emptyTypology()] }));
  const removeTypology = (idx) => setData((d) => {
    if (d.typologies.length <= 1) return d;
    const t = [...d.typologies];
    t.splice(idx, 1);
    return { ...d, typologies: t };
  });

  // ─────────────── validation por paso ───────────────
  const validateStep = (s) => {
    if (s === 1) {
      if (!data.company_name || data.company_name.length < 2) return 'Ingresa el nombre de la constructora.';
      if (!data.contact_name || data.contact_name.length < 2) return 'Ingresa el nombre del contacto.';
      if (!/^\S+@\S+\.\S+$/.test(data.contact_email)) return 'Correo del contacto inválido.';
      if (!data.contact_phone || data.contact_phone.length < 6) return 'Teléfono inválido.';
      return null;
    }
    if (s === 2) {
      if (!data.project_name || data.project_name.length < 2) return 'Ingresa el nombre del proyecto.';
      if (!data.city || data.city.length < 2) return 'Ingresa la ciudad.';
      if (!STAGES.some((x) => x.value === data.stage)) return 'Etapa inválida.';
      return null;
    }
    if (s === 3) {
      if (!Array.isArray(data.typologies) || data.typologies.length === 0)
        return 'Agrega al menos una tipología.';
      for (let i = 0; i < data.typologies.length; i++) {
        const t = data.typologies[i];
        if (!t.name || t.name.trim().length < 1) return `Tipología #${i + 1}: falta el nombre.`;
        if (!(+t.area_m2 > 0)) return `Tipología #${i + 1}: área m² inválida.`;
        if (!(Number.isInteger(+t.beds) && +t.beds >= 0)) return `Tipología #${i + 1}: habitaciones inválidas.`;
        if (!(+t.baths >= 0)) return `Tipología #${i + 1}: baños inválidos.`;
        if (!(Number.isInteger(+t.parking) && +t.parking >= 0)) return `Tipología #${i + 1}: parqueaderos inválidos.`;
        if (!(+t.price_from > 0)) return `Tipología #${i + 1}: precio desde inválido.`;
      }
      return null;
    }
    return null;
  };

  const next = () => {
    const err = validateStep(step);
    if (err) { toast({ title: 'Falta información', description: err, variant: 'destructive' }); return; }
    setStep(step + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const back = () => setStep(Math.max(1, step - 1));

  const submit = async () => {
    // Validate all previous steps
    for (const s of [1, 2, 3]) {
      const err = validateStep(s);
      if (err) { toast({ title: 'Falta información', description: err, variant: 'destructive' }); setStep(s); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        typologies: data.typologies.map((t) => ({
          name: t.name.trim(),
          area_m2: +t.area_m2,
          beds: +t.beds,
          baths: +t.baths,
          parking: +t.parking,
          price_from: +t.price_from,
        })),
      };
      // Limpia strings vacíos → null
      ['neighborhood', 'address', 'delivery_date', 'description', 'brochure_url', 'pricelist_url', 'company_website']
        .forEach((k) => { if (!payload[k]) delete payload[k]; });
      const res = await api.submitConstructorProject(payload);
      setSubmitted({
        id: res.id,
        message: res.message,
        project: res.project,
        market_analysis: res.market_analysis || null,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast({ title: 'No pudimos enviar el proyecto', description: e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────── success ───────────────
  if (submitted) {
    const analysis = submitted.market_analysis;
    const projectName = submitted.project?.project_name || 'tu proyecto';
    return (
      <div className="min-h-screen bg-habita-cream">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-14">
          <div className="text-center">
            <div className="w-20 h-20 rounded-full bg-habita-mint text-habita grid place-items-center mx-auto">
              <CheckCircle2 className="w-10 h-10" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-habita-dark mt-6">
              ¡Recibimos <span className="text-habita">{projectName}</span>!
            </h1>
            <p className="text-gray-600 mt-3">{submitted.message}</p>
            <p className="text-xs text-gray-500 mt-1">ID de referencia: <code>{submitted.id}</code></p>
          </div>

          {/* Análisis rápido del mercado */}
          {analysis && analysis.segments && analysis.segments.length > 0 && (
            <div className="mt-10 bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm" data-testid="submission-market-analysis">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 rounded-full bg-habita text-white grid place-items-center shrink-0">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-habita font-semibold">Análisis del Advisor</p>
                  <h2 className="font-serif text-2xl text-habita-dark mt-0.5">
                    Basados en el mercado actual{analysis.has_baseline ? ` de ${analysis.baseline_source}` : ''}
                  </h2>
                </div>
              </div>

              <div className="space-y-4">
                {analysis.segments.map((seg, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 p-4 bg-habita-cream/40"
                    data-testid={`market-segment-${seg.beds}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                          {seg.count} tipología{seg.count !== 1 ? 's' : ''} · {seg.label}
                        </p>
                        <p className="mt-1 font-semibold text-habita-dark leading-snug">
                          {seg.headline}
                        </p>
                        <p className="text-xs text-gray-600 mt-1.5 italic">{seg.interest_reason}</p>
                        {seg.typology_names && seg.typology_names.length > 0 && (
                          <p className="text-xs text-gray-500 mt-2">
                            Aplica a: {seg.typology_names.filter(Boolean).map((n) => (
                              <span key={n} className="inline-block bg-white border border-gray-200 rounded-full px-2 py-0.5 mr-1 mb-1">{n}</span>
                            ))}
                          </p>
                        )}
                      </div>
                      <span
                        data-testid={`interest-level-${seg.beds}`}
                        className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full shrink-0 ${
                          seg.interest_level === 'alto' ? 'bg-emerald-100 text-emerald-700' :
                          seg.interest_level === 'medio' ? 'bg-amber-100 text-amber-700' :
                          'bg-rose-100 text-rose-700'
                        }`}
                      >
                        Interés {seg.interest_level}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 text-xs text-gray-500 flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-habita shrink-0" />
                <span>
                  Estos rangos se calculan comparando tu proyecto con nuestra base de <strong>market pricing baselines</strong>
                  {' '}del sector. Sin IA generativa — 100% determinístico.
                </span>
              </div>
            </div>
          )}

          <div className="mt-10 text-center flex gap-3 justify-center flex-wrap">
            <Link to="/"><Button variant="outline" className="rounded-full h-11 px-6">Volver al inicio</Button></Link>
            <Button
              onClick={() => { setSubmitted(null); setData({ ...data, project_name: '', typologies: [emptyTypology()] }); setStep(2); }}
              className="btn-habita rounded-full h-11 px-6"
              data-testid="submit-another-btn"
            >
              Enviar otro proyecto
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-habita-cream" data-testid="constructor-panel">
      <Header />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 pt-10 pb-6">
        <div className="flex items-center gap-2 text-habita font-semibold text-xs uppercase tracking-widest">
          <Building2 className="w-4 h-4" /> Panel de Constructoras
        </div>
        <h1 className="font-serif text-3xl md:text-5xl text-habita-dark mt-3 leading-tight">
          Publica tu proyecto de vivienda nueva
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl">
          Nuestro <strong>Habita Advisor</strong> recomienda tu proyecto a compradores calificados
          basándose en las tipologías exactas que registres. Cuenta con nuestro equipo revisando y aprobando cada envío.
        </p>
        <div className="mt-4 inline-flex items-center gap-2 text-xs text-gray-500 bg-white rounded-full px-3 py-1.5 border border-gray-200">
          <ShieldCheck className="w-3.5 h-3.5 text-habita" />
          Revisión manual antes de salir al aire — sin comisiones ni suscripción durante Beta
        </div>
      </section>

      {/* Stepper */}
      <section className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-6">
          {[
            { n: 1, label: 'Constructora' },
            { n: 2, label: 'Proyecto' },
            { n: 3, label: 'Tipologías' },
            { n: 4, label: 'Material' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <div className={`flex items-center gap-1.5 ${step === s.n ? 'text-habita font-semibold' : step > s.n ? 'text-habita-dark' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 rounded-full grid place-items-center text-xs ${step > s.n ? 'bg-habita text-white' : step === s.n ? 'bg-habita-mint text-habita ring-2 ring-habita' : 'bg-gray-200 text-gray-500'}`}>
                  {step > s.n ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
                </div>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < arr.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
            </React.Fragment>
          ))}
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm">
          {/* Step 1 */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-1">
              <h2 className="font-serif text-2xl text-habita-dark">Datos de la constructora</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre de la constructora *</Label>
                  <Input value={data.company_name} onChange={(e) => setField('company_name', e.target.value)} placeholder="Ej: Vértigo Constructora" className="mt-1.5" data-testid="input-company-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Sitio web (opcional)</Label>
                  <Input value={data.company_website} onChange={(e) => setField('company_website', e.target.value)} placeholder="https://" className="mt-1.5" data-testid="input-company-website" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Contacto — nombre *</Label>
                  <Input value={data.contact_name} onChange={(e) => setField('contact_name', e.target.value)} placeholder="Ej: María Camila Rojas" className="mt-1.5" data-testid="input-contact-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Contacto — correo *</Label>
                  <Input type="email" value={data.contact_email} onChange={(e) => setField('contact_email', e.target.value)} placeholder="contacto@constructora.com" className="mt-1.5" data-testid="input-contact-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Contacto — teléfono *</Label>
                  <Input value={data.contact_phone} onChange={(e) => setField('contact_phone', e.target.value)} placeholder="+57 300 000 0000" className="mt-1.5" data-testid="input-contact-phone" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <h2 className="font-serif text-2xl text-habita-dark">Datos del proyecto</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre del proyecto *</Label>
                  <Input value={data.project_name} onChange={(e) => setField('project_name', e.target.value)} placeholder="Ej: Torres del Verde" className="mt-1.5" data-testid="input-project-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad *</Label>
                  <Input value={data.city} onChange={(e) => setField('city', e.target.value)} placeholder="Ej: Bogotá" className="mt-1.5" data-testid="input-city" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Barrio (opcional)</Label>
                  <Input value={data.neighborhood} onChange={(e) => setField('neighborhood', e.target.value)} placeholder="Ej: Chapinero" className="mt-1.5" data-testid="input-neighborhood" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Dirección (opcional)</Label>
                  <Input value={data.address} onChange={(e) => setField('address', e.target.value)} placeholder="Ej: Cra 15 # 90-20" className="mt-1.5" data-testid="input-address" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Etapa *</Label>
                  <select
                    value={data.stage}
                    onChange={(e) => setField('stage', e.target.value)}
                    className="mt-1.5 h-10 w-full rounded-md border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-habita"
                    data-testid="input-stage"
                  >
                    {STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Entrega estimada (opcional)</Label>
                  <Input value={data.delivery_date} onChange={(e) => setField('delivery_date', e.target.value)} placeholder="Ej: 2027 Q3" className="mt-1.5" data-testid="input-delivery" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-widest text-gray-500">Descripción (opcional)</Label>
                  <Textarea value={data.description} onChange={(e) => setField('description', e.target.value)} rows={4} placeholder="Cuéntanos sobre el proyecto: amenidades, ubicación, arquitectura..." className="mt-1.5" data-testid="input-description" />
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Tipologías */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h2 className="font-serif text-2xl text-habita-dark">Tipologías del proyecto</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Esta información es la <strong>fuente de verdad</strong> que usa nuestro Advisor para
                    recomendar tu proyecto a compradores calificados.
                  </p>
                </div>
                <div className="text-xs bg-habita-mint text-habita-dark rounded-full px-3 py-1 font-semibold">
                  {data.typologies.length} tipología{data.typologies.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="space-y-4">
                {data.typologies.map((t, idx) => (
                  <div key={idx} className="rounded-xl border border-gray-200 bg-habita-cream/50 p-4" data-testid={`typology-${idx}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-semibold text-habita-dark">Tipología #{idx + 1}</p>
                      {data.typologies.length > 1 && (
                        <button
                          onClick={() => removeTypology(idx)}
                          className="text-rose-600 hover:bg-rose-50 rounded-full p-1.5"
                          data-testid={`remove-typology-${idx}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="col-span-2 md:col-span-3">
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre / Ref. *</Label>
                        <Input value={t.name} onChange={(e) => setTypology(idx, 'name', e.target.value)} placeholder="Ej: Tipo 201 · Penthouse" className="mt-1.5" data-testid={`typology-${idx}-name`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Área m² *</Label>
                        <Input type="number" min="1" value={t.area_m2} onChange={(e) => setTypology(idx, 'area_m2', e.target.value)} placeholder="Ej: 65" className="mt-1.5" data-testid={`typology-${idx}-area`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Habitaciones *</Label>
                        <Input type="number" min="0" value={t.beds} onChange={(e) => setTypology(idx, 'beds', e.target.value)} placeholder="2" className="mt-1.5" data-testid={`typology-${idx}-beds`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Baños *</Label>
                        <Input type="number" min="0" step="0.5" value={t.baths} onChange={(e) => setTypology(idx, 'baths', e.target.value)} placeholder="2" className="mt-1.5" data-testid={`typology-${idx}-baths`} />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Parqueaderos *</Label>
                        <Input type="number" min="0" value={t.parking} onChange={(e) => setTypology(idx, 'parking', e.target.value)} placeholder="1" className="mt-1.5" data-testid={`typology-${idx}-parking`} />
                      </div>
                      <div className="col-span-2 md:col-span-3">
                        <Label className="text-xs uppercase tracking-widest text-gray-500">Precio desde (COP) *</Label>
                        <Input type="number" min="0" value={t.price_from} onChange={(e) => setTypology(idx, 'price_from', e.target.value)} placeholder="Ej: 380000000" className="mt-1.5" data-testid={`typology-${idx}-price`} />
                        {t.price_from > 0 && <p className="text-xs text-gray-500 mt-1">≈ ${fmtCOP(t.price_from)} COP</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={addTypology} variant="outline" className="rounded-full" data-testid="add-typology-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Agregar otra tipología
              </Button>
            </div>
          )}

          {/* Step 4 — Material de apoyo */}
          {step === 4 && (
            <div className="space-y-6" data-testid="step-4">
              <div>
                <h2 className="font-serif text-2xl text-habita-dark">Material de apoyo</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Puedes subir un PDF o pegar un enlace externo. Es material de apoyo para el equipo interno
                  y para futuros compradores — la <strong>inteligencia del Advisor se basa en las tipologías</strong> del paso anterior.
                </p>
              </div>

              <DocSlot
                icon={<FileText className="w-4 h-4" />}
                title="Brochure del proyecto"
                url={data.brochure_url}
                onChange={(v) => setField('brochure_url', v)}
                kind="brochure"
                testId="brochure"
              />
              <DocSlot
                icon={<DollarSign className="w-4 h-4" />}
                title="Lista de precios"
                url={data.pricelist_url}
                onChange={(v) => setField('pricelist_url', v)}
                kind="pricelist"
                testId="pricelist"
              />

              <div className="bg-habita-mint/40 border border-habita/20 rounded-xl p-4 text-sm text-habita-dark">
                <p className="font-semibold flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-habita" /> Próximos pasos
                </p>
                <ol className="mt-2 space-y-1 text-sm list-decimal list-inside">
                  <li>Nuestro equipo revisa tu proyecto en un plazo máximo de 48 horas.</li>
                  <li>Te contactamos al correo indicado con la confirmación o solicitud de ajustes.</li>
                  <li>Una vez aprobado, el Advisor comenzará a recomendarlo automáticamente.</li>
                </ol>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="mt-8 flex items-center justify-between gap-3 border-t border-gray-100 pt-6">
            {step > 1 ? (
              <Button variant="outline" onClick={back} className="rounded-full h-11 px-5" data-testid="back-btn">
                <ChevronLeft className="w-4 h-4 mr-1.5" /> Atrás
              </Button>
            ) : <span />}
            {step < 4 ? (
              <Button onClick={next} className="btn-habita rounded-full h-11 px-6" data-testid="next-btn">
                Siguiente <ChevronRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting} className="btn-habita rounded-full h-11 px-6" data-testid="submit-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Enviando...</> : 'Enviar proyecto'}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

// ─────────────────────────── DocSlot ───────────────────────────

function DocSlot({ icon, title, url, onChange, kind, testId }) {
  const { toast } = useToast();
  const [mode, setMode] = useState('link'); // 'link' | 'file'
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast({ title: 'Archivo muy grande', description: 'Máximo 20MB.', variant: 'destructive' });
      return;
    }
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      toast({ title: 'Formato no soportado', description: 'Sube un PDF.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const res = await api.uploadConstructorDoc(file, kind);
      onChange(res.url);
      toast({ title: 'PDF subido', description: 'El documento quedó listo.' });
    } catch (err) {
      toast({ title: 'No pudimos subir el PDF', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4" data-testid={`doc-slot-${testId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-semibold text-habita-dark flex items-center gap-1.5">
          <span className="w-8 h-8 rounded-full bg-habita-mint text-habita grid place-items-center">{icon}</span>
          {title}
        </p>
        <div className="inline-flex rounded-full bg-gray-100 p-0.5 text-xs">
          <button
            onClick={() => setMode('link')}
            className={`px-3 py-1 rounded-full ${mode === 'link' ? 'bg-white shadow-sm font-semibold' : 'text-gray-500'}`}
            data-testid={`${testId}-mode-link`}
          >
            <LinkIcon className="w-3 h-3 inline mr-1" /> Link
          </button>
          <button
            onClick={() => setMode('file')}
            className={`px-3 py-1 rounded-full ${mode === 'file' ? 'bg-white shadow-sm font-semibold' : 'text-gray-500'}`}
            data-testid={`${testId}-mode-file`}
          >
            <Upload className="w-3 h-3 inline mr-1" /> PDF
          </button>
        </div>
      </div>

      {mode === 'link' ? (
        <Input
          value={url}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="mt-3"
          data-testid={`${testId}-link-input`}
        />
      ) : (
        <div className="mt-3">
          <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-habita font-semibold hover:underline">
            <input type="file" accept="application/pdf,.pdf" hidden onChange={handleFile} data-testid={`${testId}-file-input`} />
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Subiendo...</> : <><Upload className="w-4 h-4" /> Seleccionar PDF (máx 20MB)</>}
          </label>
        </div>
      )}

      {url && (
        <p className="text-xs text-gray-500 mt-2 truncate">
          <span className="font-semibold text-habita-dark">Actual:</span> <a href={url} target="_blank" rel="noreferrer" className="text-habita hover:underline">{url}</a>
        </p>
      )}
    </div>
  );
}
